import { Shield, Envelope as Mail, Clock, Trash as Trash2, Warning as AlertTriangle } from '@phosphor-icons/react';
import { Helmet } from "react-helmet-async";
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';


export default function DeleteAccount() {
  const __roBlocked = useRoOnlyGuard();
  return (
    <>
      <Helmet>
        <title>Ștergere cont — RO Auto Market</title>
        <meta name="description" content="Solicită ștergerea contului tău RO Auto Market și a datelor asociate." />
      </Helmet>
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Ștergere cont</h1>
          </div>

          <p className="text-muted-foreground mb-8">
            RO Auto Market respectă dreptul tău la ștergerea datelor conform GDPR. 
            Poți solicita ștergerea completă a contului și a datelor tale asociate urmând pașii de mai jos.
          </p>

          {/* Steps */}
          <section className="space-y-6 mb-10">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Pași pentru ștergerea contului
            </h2>

            <ol className="space-y-4 list-none">
              <li className="flex gap-4 bg-card border border-border rounded-lg p-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">1</span>
                <div>
                  <p className="font-medium text-foreground">Trimite un email la adresa de mai jos</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Scrie-ne la <a href="mailto:contact@roautomarket.ro" className="text-primary underline font-medium">contact@roautomarket.ro</a> cu 
                    subiectul <strong>„Ștergere cont"</strong>.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 bg-card border border-border rounded-lg p-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">2</span>
                <div>
                  <p className="font-medium text-foreground">Include adresa de email a contului</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Trimite cererea de pe adresa de email asociată contului tău, pentru verificarea identității.
                  </p>
                </div>
              </li>
              <li className="flex gap-4 bg-card border border-border rounded-lg p-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">3</span>
                <div>
                  <p className="font-medium text-foreground">Primești confirmare</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Vom procesa cererea și îți vom trimite o confirmare pe email în termen de 72 de ore.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          {/* Data deleted */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Date care vor fi șterse</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Profilul contului (email, nume, preferințe)</li>
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Anunțurile publicate și fotografiile asociate</li>
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Mesajele și conversațiile</li>
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Alertele de preț și căutările salvate</li>
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Profilul de dealer (dacă există)</li>
              <li className="flex items-start gap-2"><span className="text-destructive mt-0.5">✕</span> Cookie-urile și datele de analytics asociate</li>
            </ul>
          </section>

          {/* Data retained */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Date păstrate temporar
            </h2>
            <div className="bg-muted/50 border border-border rounded-lg p-4 text-sm text-muted-foreground space-y-2">
              <p>
                <strong>Loguri de securitate</strong> — păstrate 90 de zile conform obligațiilor legale 
                (prevenirea fraudei, conformitate GDPR Art. 17(3)).
              </p>
              <p>
                <strong>Date de facturare Stripe</strong> — păstrate conform cerințelor fiscale (maxim 5 ani), 
                dar nu mai sunt asociate contului tău.
              </p>
              <p>
                <strong>Recenzii anonimizate</strong> — textul recenziilor poate fi păstrat în formă anonimizată, 
                fără date personale.
              </p>
            </div>
          </section>

          {/* Timeline */}
          <section className="mb-8">
            <div className="flex items-start gap-3 bg-muted/50 border border-border rounded-lg p-4">
              <AlertTriangle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground">Perioadă de procesare</p>
                <p className="text-muted-foreground mt-1">
                  Ștergerea contului este procesată în maxim <strong>30 de zile</strong> de la confirmarea cererii.
                  Acțiunea este ireversibilă — toate datele vor fi șterse permanent.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="text-center border-t border-border pt-8">
            <Mail className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Contact: <a href="mailto:contact@roautomarket.ro" className="text-primary underline">contact@roautomarket.ro</a>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              RO Auto Market — Marketplace verificat de vehicule
            </p>
          </section>
        </div>
      </main>
    </>
  );
}
